import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Divider,
	Form,
	Image,
	Message,
	Tab
} from 'semantic-ui-react';

import {
	log,
	print
} from '../../../../../services/utils';

import { updateProgramPhoto } from '../../../../../api/nora/programs';
import { uploadAsset } from '../../../../../api/nora/assets';

import { useAuthState } from '../../../../../contexts/AuthContext';
import { useProgramState } from '../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import DDFileUploadModal from '../../../../DDFileUpload';
import FormDatePicker from '../../../../Form/FormDatePicker';
import FormInput from '../../../../Form/FormInput';
import FormSelect from '../../../../Form/FormSelect';
import FormTextArea from '../../../../Form/FormTextArea';
import FormErrors from '../../../../Shared/FormErrors';

const InfoTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const {
		programTypes,
		programStatuses
	} = useProgramState();
	const { setApiErrorAlert } = useUIAction();

	const [ fileUploadModal, setFileUploadModal ] = useState( false );
	const [ editable, setEditable ] = useState( !formData.id );
	const [ removeLogoConfirmAlert, setRemoveLogoConfirmAlert ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( ( error, i ) => error.error
			? <Message.Item key={ `${ error.fieldLabel }-${ i }` }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Program'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/operations/programs' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editPrograms.canExecute,
		icon: 'pencil',
		permission: permissions.editPrograms.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: permissions.viewPrograms.canExecute,
		text: 'Print'
	} ), [
		permissions,
		validate
	] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				printAction,
				editAction
			],
		[
			cancelAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	const handleUploadLogo = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	const onUpload = useCallback( async ( { uid } ) => {
		if ( formData.id ) {
			try {
				const response = await updateProgramPhoto( formData.id, uid );

				updateField( {
					target: {
						name: 'logo',
						value: response.uri
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const handleRemoveLogo = useCallback( () => {
		setRemoveLogoConfirmAlert( true );
	}, [] );

	const removeLogo = useCallback( () => {
		if ( formData.id ) {
			try {
				updateProgramPhoto( formData.id, 0 );

				updateField( {
					target: {
						name: 'logo',
						value: null
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const closeFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const closeRemoveLogoConfirmAlert = useCallback( () => {
		setRemoveLogoConfirmAlert( false );
	}, [] );

	return (
		<Tab.Pane as="div">
			{ log( 'infoTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<div className="no-print">
					<Image
						size="small"
						src={ formData.logo
							? formData.logo
							: 'https://react.semantic-ui.com/images/wireframe/image.png'
						}
					/>
					{ editable
						? formData.logo
							? <small className="image-upload-actions">
								<span onClick={ handleUploadLogo }>edit</span>
								&nbsp;&nbsp;|&nbsp;&nbsp;
								<span onClick={ handleRemoveLogo }>remove</span>
							</small>
							: <small className="image-upload-actions">
								<span onClick={ handleUploadLogo }>upload</span>
							</small>
						: null
					}
				</div>

				<Form
					autoComplete="off"
					error={ forceValidation && validate() }
				>
					<input
						className="hidden-input"
						name="name"
					/>
					<Form.Group>
						<FormInput
							errors={ errors.name }
							fieldLabel="Program Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="name"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.name || '' }
							width={ 10 }
						/>
						<FormSelect
							errors={ errors.type }
							fieldLabel="Program Type"
							forceValidation={ forceValidation }
							name="type"
							onChange={ updateField }
							options={ programTypes }
							readOnly={ !editable }
							required
							value={ formData.type || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormDatePicker
							errors={ errors.startDate }
							fieldLabel="Start Date"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="startDate"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.startDate || '' }
							width={ 5 }
						/>
						<FormDatePicker
							errors={ errors.endDate }
							fieldLabel="End Date"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="endDate"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.endDate || '' }
							width={ 5 }
						/>
						<FormSelect
							errors={ errors.submissionStatus }
							fieldLabel="Program Status"
							forceValidation={ forceValidation }
							name="submissionStatus"
							onChange={ updateField }
							options={ programStatuses }
							readOnly={ !editable }
							required
							value={ formData.submissionStatus || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormTextArea
							fieldLabel="Description"
							name="description"
							onChange={ updateField }
							readOnly={ !editable }
							rows={ 6 }
							value={ formData.description || '' }
							width={ 16 }
						/>
					</Form.Group>
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
			<DDFileUploadModal
				closeModal={ closeFileUploadModal }
				onUpload={ onUpload }
				open={ fileUploadModal }
				upload={ uploadAsset }
			/>
			<Alert
				action={ removeLogo }
				actionText="Remove Logo"
				name="program-remove-logo-confirm-alert"
				message="Are you sure?"
				open={ removeLogoConfirmAlert }
				closeModal={ closeRemoveLogoConfirmAlert }
				closeText="Cancel"
				type="confirm"
			/>
		</Tab.Pane>
	);
};

InfoTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( InfoTab );
